import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ChevronDown, Users, Instagram, Shield, ClipboardList, BarChart3, Send, Crown, Check, Zap, Sparkles, Star, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Logo } from '@/components/Logo';
import { ThemeToggle } from '@/components/ThemeToggle';
import { SeoHead } from '@/components/SeoHead';
import { AppDemoPhone } from '@/components/AppDemoPhone';


const FAQS = [
  {
    q: 'Is Sportara really free?',
    a: 'Yes! The core training experience is completely free. We offer optional premium content for those who want to go deeper.',
  },
  {
    q: 'Do I need to know anything about sports to start?',
    a: 'Not at all. We start from scratch — rules, positions, scoring. Our assessment places you at the right level.',
  },
  {
    q: 'How long are the lessons?',
    a: 'Most lessons take 2–5 minutes. Short and focused — designed to fit into your day.',
  },
];

const LEVELS = [
  { emoji: '🌱', label: 'Brand New', desc: 'Never watched a game' },
  { emoji: '⭐', label: 'Casual Fan', desc: 'Watch but want to learn more' },
  { emoji: '🧠', label: 'Enthusiast', desc: 'Ready for deeper strategy' },
];

function FaqItem({ question, answer, index }: { question: string; answer: string; index: number }) {
  const [open, setOpen] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ delay: index * 0.08, duration: 0.5 }}
    >
      <button
        onClick={() => setOpen(!open)}
        className="glass-card w-full p-5 text-left shadow-elevated"
      >
        <div className="flex items-center justify-between gap-3">
          <h3 className="font-display font-bold text-base sm:text-lg text-foreground">{question}</h3>
          <ChevronDown className={`w-5 h-5 text-muted-foreground shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
        </div>
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <p className="text-sm sm:text-base text-muted-foreground font-body leading-relaxed mt-3 pt-3 border-t border-white/10">
                {answer}
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </button>
    </motion.div>
  );
}


interface LandingPageProps {
  onGetStarted: () => void;
  onSignIn?: () => void;
  onContact: () => void;
  onTerms?: () => void;
  onPrivacy?: () => void;
  /** Opens the coach signup flow directly (skipping role choice). */
  onCoachSignup: () => void;
}

export function LandingPage({ onGetStarted, onSignIn, onContact, onTerms, onPrivacy, onCoachSignup }: LandingPageProps) {
  const [demoStep, setDemoStep] = useState(0);
  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  return (
    <div className="dark min-h-screen premium-page-bg text-foreground">
      <SeoHead
        title="Sportara Coaches Hub — Lessons for Youth Teams"
        description="Coaches Hub helps youth coaches invite players, assign lessons and track progress. Free during private beta."
        path="/"
        jsonLd={faqJsonLd}
      />

      {/* Nav */}
      <nav className="bg-transparent border-b border-white/5 backdrop-blur-md">
        <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Logo className="w-8 h-8" alt="Sportara" />
            <span className="font-display font-bold text-lg tracking-tight text-foreground">Sportara</span>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/guides/football-rules-beginners"
              className="hidden sm:inline text-sm font-display font-bold text-foreground/70 hover:text-primary transition-colors"
            >
              Guides
            </Link>
            <a href="#coaches" className="text-sm font-display font-bold text-primary">For Coaches</a>
            <ThemeToggle />
            <Button
              onClick={onSignIn ?? onGetStarted}
              size="sm"
              variant="ghost"
              className="font-display font-bold text-sm rounded-[12px]"
            >
              Sign In
            </Button>
            <Button
              onClick={onCoachSignup}
              size="sm"
              className="shimmer-cta font-display font-bold bg-primary hover:bg-primary/90 text-primary-foreground rounded-[12px]"
            >
              Set up team
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="hero-animated-bg text-foreground pb-24 pt-16 overflow-hidden">

        <span className="hero-glow-c" aria-hidden="true" />
        <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            {/* Left: copy */}
            <motion.div
              initial="hidden"
              animate="show"
              variants={{
                hidden: {},
                show: { transition: { staggerChildren: 0.1 } },
              }}
              className="text-center lg:text-left"
            >
              <motion.span
                variants={{ hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0 } }}
                className="inline-block bg-primary/15 text-primary font-display font-bold text-xs px-3 py-1 rounded-full mb-6 uppercase tracking-wider"
              >
                PRIVATE BETA · FOR YOUTH COACHES
              </motion.span>
              <motion.h1
                variants={{ hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0 } }}
                className="font-display font-bold text-4xl sm:text-5xl md:text-6xl leading-tight mb-4"
              >
                Coaches Hub:<br />
                <span className="text-gradient-primary">Build smarter players.</span>
              </motion.h1>
              <motion.p
                variants={{ hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0 } }}
                className="font-body text-secondary-foreground/70 text-base sm:text-lg max-w-xl mx-auto lg:mx-0 mb-8 leading-relaxed"
              >
                Assign lessons, track who did them, and help your youth team learn together.
              </motion.p>
              <motion.div
                variants={{ hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0 } }}
                className="flex flex-col sm:flex-row gap-3 sm:items-center justify-center lg:justify-start"
              >
                <Button
                  onClick={onCoachSignup}
                  size="lg"
                  className="h-14 w-full max-w-full px-5 text-base font-display font-bold bg-primary hover:bg-primary/90 text-primary-foreground rounded-[12px] shadow-glow-primary sm:w-auto sm:px-8 sm:text-lg"
                >
                  <span className="whitespace-nowrap">Set up your team free</span>
                  <ArrowRight className="w-5 h-5 shrink-0" />
                </Button>
                <Button variant="outline" size="lg" onClick={() => { window.location.assign('/try'); }}>Try a lesson</Button>
                <Button variant="outline" size="lg" onClick={() => document.getElementById('how-coaching-works')?.scrollIntoView({ behavior: 'smooth' })}>See how it works</Button>
                <p className="text-xs text-secondary-foreground/50 font-body">
                  Free during beta · No credit card required
                </p>
              </motion.div>

              {/* Social proof — honest beta copy, no fabricated numbers */}
              <motion.div
                variants={{ hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0 } }}
                className="mt-10 flex items-center justify-center lg:justify-start gap-2"
              >
                <Sparkles className="w-4 h-4 text-primary shrink-0" />
                <p className="text-xs sm:text-sm font-body text-secondary-foreground/60">
                  Free during beta — <span className="font-display font-bold text-secondary-foreground/80">no credit card required.</span>
                </p>
              </motion.div>

            </motion.div>

            {/* Right: floating mockup */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.7 }}
              className="hidden lg:flex justify-center"
            >
              <div className="float-bob">
                <div
                  className="relative w-[280px] h-[560px] rounded-[44px] p-3 bg-gradient-to-br from-white/10 to-white/5 border border-white/15"
                  style={{ boxShadow: '0 30px 60px -10px rgba(0,0,0,0.5), 0 0 40px hsl(221 83% 53% / 0.25)' }}
                >
                  <div className="absolute top-3 left-1/2 -translate-x-1/2 w-24 h-5 bg-black/70 rounded-b-2xl z-10" />
                  <div className="w-full h-full rounded-[36px] bg-[#0f1729] overflow-hidden flex flex-col p-4 pt-10">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-[10px] font-display font-bold text-white/60 uppercase tracking-wider">Soccer · Lesson 4</span>
                      <span className="text-[10px] font-display font-bold text-primary">+15 XP</span>
                    </div>
                    <h3 className="font-display font-bold text-white text-base leading-tight mb-3">
                      Read the 4-3-3 Press
                    </h3>
                    {/* Tactical pitch */}
                    <div className="relative rounded-xl bg-gradient-to-b from-emerald-700 to-emerald-900 h-44 mb-3 overflow-hidden border border-white/10">
                      <div className="absolute inset-0 opacity-30" style={{ backgroundImage: 'repeating-linear-gradient(90deg, transparent 0 18px, rgba(255,255,255,0.15) 18px 19px)' }} />
                      <div className="absolute inset-2 border border-white/40 rounded" />
                      <div className="absolute top-1/2 left-2 right-2 border-t border-white/40" />
                      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full border border-white/40" />
                      {/* Players */}
                      {[
                        { l: '20%', t: '20%', c: 'bg-blue-400' },
                        { l: '50%', t: '15%', c: 'bg-blue-400' },
                        { l: '80%', t: '20%', c: 'bg-blue-400' },
                        { l: '30%', t: '45%', c: 'bg-blue-400' },
                        { l: '70%', t: '45%', c: 'bg-blue-400' },
                        { l: '25%', t: '75%', c: 'bg-rose-400' },
                        { l: '50%', t: '70%', c: 'bg-rose-400' },
                        { l: '75%', t: '75%', c: 'bg-rose-400' },
                      ].map((p, i) => (
                        <div key={i} className={`absolute w-3 h-3 rounded-full ${p.c} ring-2 ring-white/30`} style={{ left: p.l, top: p.t, transform: 'translate(-50%,-50%)' }} />
                      ))}
                      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                        <path d="M 30 45 Q 40 30 50 25" stroke="white" strokeWidth="0.6" fill="none" strokeDasharray="2 2" opacity="0.7" />
                        <path d="M 70 45 Q 60 30 50 25" stroke="white" strokeWidth="0.6" fill="none" strokeDasharray="2 2" opacity="0.7" />
                      </svg>
                    </div>
                    <p className="text-[11px] font-body text-white/70 mb-2 leading-snug">
                      Which player triggers the press first?
                    </p>
                    <div className="space-y-1.5">
                      {['Striker (CF)', 'Left winger', 'Holding midfielder'].map((opt, i) => (
                        <div
                          key={opt}
                          className={`text-[11px] font-display font-bold px-3 py-2 rounded-lg border ${
                            i === 0
                              ? 'bg-primary/20 border-primary/50 text-white'
                              : 'bg-white/5 border-white/10 text-white/70'
                          } flex items-center justify-between`}
                        >
                          <span>{opt}</span>
                          {i === 0 && <CheckCircle2 className="w-3.5 h-3.5 text-primary" />}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

        </div>
      </section>


      <section id="how-coaching-works" className="py-16 border-y border-border scroll-mt-10">
        <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-8">
          <h2 className="font-display font-bold text-3xl mb-8">How coaching works</h2>
          <div className="grid sm:grid-cols-3 gap-8">{[
            ['1', 'Set up your team', 'Create your account and name your team.'],
            ['2', 'Invite players', 'Text parents one link to join your team.'],
            ['3', 'Assign & follow up', 'Assign a lesson and see who finished it.'],
          ].map(([number, title, copy]) => <div key={number} className="border-t-2 border-primary pt-4"><span className="font-display font-bold text-primary">{number}</span><h3 className="font-display font-bold text-xl mt-2">{title}</h3><p className="text-muted-foreground mt-2">{copy}</p></div>)}</div>
          <Button onClick={onCoachSignup} className="mt-8">Set up your team free <ArrowRight className="w-4 h-4 ml-2" /></Button>
        </div>
      </section>

      {/* Coaches Hub */}
      <section id="coaches" className="py-20 scroll-mt-10">

        <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <span className="inline-block bg-accent/15 text-accent font-display font-bold text-xs px-3 py-1 rounded-full mb-4 uppercase tracking-wider">
              For Coaches
            </span>
            <h2 className="font-display font-bold text-3xl sm:text-4xl mb-3">
              Coaches <span className="text-primary">Hub</span>
            </h2>
            <p className="font-body text-muted-foreground text-sm sm:text-base max-w-xl mx-auto leading-relaxed">
              A dedicated dashboard to manage your team's learning — assign lessons, track progress, and build smarter players.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {[
              {
                icon: <Users className="w-5 h-5" />,
                title: 'Team Management',
                desc: 'Create teams, add players with join codes, and manage your full roster in one place.',
              },
              {
                icon: <ClipboardList className="w-5 h-5" />,
                title: 'Assign Lessons',
                desc: 'Hand-pick lessons or build your own and assign them directly to your team.',
              },
              {
                icon: <BarChart3 className="w-5 h-5" />,
                title: 'Track Completion',
                desc: 'See which players have finished assignments with real-time progress tracking.',
              },
              {
                icon: <Send className="w-5 h-5" />,
                title: 'Instant Alerts',
                desc: 'Players get notified the moment you assign new lessons — nothing gets missed.',
              },
            ].map((feature, i) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="glass-card p-5"
              >
                <div className="w-10 h-10 rounded-[12px] bg-primary/10 flex items-center justify-center text-primary mb-3">
                  {feature.icon}
                </div>
                <h3 className="font-display font-bold text-sm mb-1">{feature.title}</h3>
                <p className="text-xs text-muted-foreground font-body leading-relaxed">{feature.desc}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="glass-card p-6 max-w-2xl mx-auto mt-8"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-accent/15 flex items-center justify-center shrink-0">
                <Shield className="w-6 h-6 text-accent" />
              </div>
              <div>
                <h3 className="font-display font-bold text-base mb-1">Made for youth coaches</h3>
                <p className="text-sm text-muted-foreground font-body leading-relaxed">
                  Instead of printing playbooks or sending PDFs, assign interactive lessons your players actually complete. 
                  Track who's putting in the work and identify knowledge gaps — all from your phone. 
                  Free to start, with optional tiers for larger programs.
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5 }}
            className="text-center mt-8"
          >
            <Button
              onClick={onCoachSignup}
              size="lg"
              className="shimmer-cta h-14 px-8 text-lg font-display font-bold bg-primary hover:bg-primary/90 text-primary-foreground rounded-[12px] shadow-glow-primary"
            >
              Set up your team free <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <p className="text-xs text-muted-foreground font-body mt-2">Free during beta — 1 team & 6 assignments included</p>
          </motion.div>
        </div>
      </section>

      {/* Also for athletes & fans */}
      <section className="py-20">

        <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="font-display font-bold text-3xl sm:text-4xl mb-3">
              Also for <span className="text-primary">athletes & fans</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-3xl mx-auto">
            {[
              { emoji: '🌱', title: 'New fans', highlight: 'Start from zero' },
              { emoji: '🏃', title: 'Athletes', highlight: 'Sharpen your IQ' },
              { emoji: '👨‍👩‍👧', title: 'Sports parents', highlight: 'Follow every play' },
              { emoji: '📺', title: 'Lifelong fans', highlight: 'Go deeper' },
            ].map((persona, i) => (
              <motion.div
                key={persona.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="glass-card p-4 text-center"
              >
                <div className="text-3xl mb-2">{persona.emoji}</div>
                <h3 className="font-display font-bold text-sm mb-1">{persona.title}</h3>
                <span className="text-[11px] font-display font-bold text-primary">
                  {persona.highlight}
                </span>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="text-center mt-8"
          >
            <Button
              onClick={onGetStarted}
              className="shimmer-cta h-12 px-6 font-display font-bold bg-primary hover:bg-primary/90 text-primary-foreground rounded-[12px] shadow-glow-primary"
            >
              Find Your Starting Point <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Early access — honest beta callout (no fabricated testimonials) */}
      <section className="py-20 text-foreground">
        <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card p-8 sm:p-10 max-w-2xl mx-auto text-center shadow-elevated ring-1 ring-primary/30"
          >
            <span className="inline-block bg-primary/15 text-primary font-display font-bold text-xs px-3 py-1 rounded-full mb-5 uppercase tracking-wider">
              Private Beta
            </span>
            <h2 className="font-display font-bold text-2xl sm:text-3xl mb-3">
              We're in beta — and looking for our first coaches
            </h2>
            <p className="font-body text-sm sm:text-base text-muted-foreground max-w-lg mx-auto leading-relaxed mb-7">
              Coaches Hub is free during beta, and we're offering personal help to a small group of youth coaches. Set up your team yourself, or contact us for a hand.
            </p>
            <Button
              onClick={onCoachSignup}
              className="shimmer-cta h-12 px-6 font-display font-bold bg-primary hover:bg-primary/90 text-primary-foreground rounded-[12px] shadow-glow-primary"
            >
              Get Early Access <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </motion.div>

        </div>
      </section>

      {/* FAQ */}
      <section className="py-20">
        <div className="max-w-2xl mx-auto px-5 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="font-display font-bold text-3xl sm:text-4xl mb-3">
              Questions?
            </h2>
          </motion.div>

          <div className="space-y-3">
            {FAQS.map((faq, i) => (
              <FaqItem key={i} question={faq.q} answer={faq.a} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 text-foreground text-center">
        <div className="max-w-xl mx-auto px-5 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="font-display font-bold text-3xl sm:text-4xl mb-3">
              Ready to <span className="text-primary">set up your team</span>?
            </h2>
            <p className="text-secondary-foreground/50 font-body mb-8">
              Free during beta — no credit card required.
            </p>
            <Button
              onClick={onCoachSignup}
              size="lg"
              className="shimmer-cta h-14 px-8 text-lg font-display font-bold bg-primary hover:bg-primary/90 text-primary-foreground rounded-[12px] shadow-glow-primary"
            >
              Set up your team free <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black/30 backdrop-blur-md border-t border-white/10 text-foreground py-10">
        <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-secondary-foreground/40">
              <Logo className="w-5 h-5 opacity-50" alt="Sportara" />
              <span className="font-display font-bold text-xs">Sportara</span>
              <span className="text-xs font-body">· Train your mind. Sharpen your game.</span>
            </div>
            <div className="flex items-center gap-4 flex-wrap">
              <Link
                to="/guides/football-rules-beginners"
                className="text-xs font-display font-bold text-secondary-foreground/50 hover:text-primary transition-colors"
              >
                Guides
              </Link>
              <a href="mailto:appsportara@gmail.com" className="text-xs text-muted-foreground hover:text-primary">appsportara@gmail.com</a>
              <button
                onClick={onContact}
                className="text-xs font-display font-bold text-secondary-foreground/50 hover:text-primary transition-colors"
              >
                Contact Us
              </button>
              {onTerms && (
                <button
                  onClick={onTerms}
                  className="text-xs font-display font-bold text-secondary-foreground/50 hover:text-primary transition-colors"
                >
                  Terms of Service
                </button>
              )}
              {onPrivacy && (
                <button
                  onClick={onPrivacy}
                  className="text-xs font-display font-bold text-secondary-foreground/50 hover:text-primary transition-colors"
                >
                  Privacy Policy
                </button>
              )}
              <a
                href="https://instagram.com/sportaraapp"
                target="_blank"
                rel="noopener noreferrer"
                className="text-secondary-foreground/50 hover:text-primary transition-colors"
                aria-label="Follow us on Instagram"
              >
                <Instagram className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
